import {
  Block,
  BlockRelationship,
  RootBlock,
  areBRsCompatible,
  blockRelationshipBaseOps,
} from '@basehub/data/replicache/index'
import { areBlocksCompatible } from '@basehub/data/replicache/schema/block/compare'
import { invariant } from '@epic-web/invariant'
import pMap from 'p-map'
import { WriteTransaction } from 'replicache'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { deleteBlocks } from '../block/mutators/crud'
import { getId } from '@basehub/utils/id/index'
import { BlockBatchMutationParams } from '../block/mutators/batch'
import { tryCatch } from '@basehub/utils'
import type { PerfReport } from '@basehub/utils/perf/index'

export type FastForwardInput = {
  rootBlockId: string
  upserted: Array<{ br: BlockRelationship; block: Block }>
  deleted: Array<string>
  now: string
  undo?: BlockBatchMutationParams['mutations']
  perfReporter?: PerfReport
}

export async function fastForward(
  tx: WriteTransaction,
  { rootBlockId, upserted, deleted, undo, now, perfReporter }: FastForwardInput
) {
  ensureBaseHubTransaction(tx)
  const treeManager = await tx.getTreeManager(rootBlockId)
  const brsInRoot = await tx.getBrsByRootId(treeManager.root.value as RootBlock)

  perfReporter?.checkpoint('[fast forward] getTreeManager')

  await pMap(upserted, async ({ block, br }) => {
    const [originalBlock, originalBr] = await Promise.all([
      treeManager.getNode(block.id)?.value ?? blockBaseOps.get(tx, block.id),
      brsInRoot.get(br.id) ?? blockRelationshipBaseOps.get(tx, br.id),
    ])

    const debug = false
    if (debug) {
      console.log({ originalBr, br, block, originalBlock })
    }

    if (
      !originalBr ||
      !areBRsCompatible(originalBr, br) ||
      originalBr.discardedAt ||
      originalBr.deletedAt
    ) {
      if (debug) {
        console.log('updating br', { originalBr, br })
      }
      invariant(!br.isCommitted, 'block relationship should not be committed')
      delete br.discardedAt
      delete br.deletedAt
      await blockRelationshipBaseOps.set(tx, br)

      if (originalBr) {
        undo?.push({ op: 'updateBR', args: originalBr })
      } else {
        // delete
        undo?.push({ op: 'updateBR', args: { ...br, deletedAt: now } })
      }
    }

    if (!originalBlock || !areBlocksCompatible(originalBlock, block)) {
      if (debug) {
        console.log('updating block', { originalBlock, block })
        console.log(
          'putting because',
          originalBlock ? 'not compatible' : 'original block not found'
        )
      }
      invariant(!block.isCommitted, 'block should not be committed')
      await blockBaseOps.put(tx, block, br.parentId)

      if (originalBlock) {
        undo?.push({ op: 'updateBlockRaw', args: originalBlock })
      } else {
        // delete
        undo?.push({
          op: 'updateBlockRaw',
          args: { ...block, deletedAt: now },
        })
      }
    }

    const existingNode = treeManager.getNode(br.sourceBlockId)
    if (existingNode) {
      // need to delete and add (and re-parent children)
      const prevChildrenKeys =
        tryCatch(() => treeManager.getChildrenKeys(existingNode.key)).result ??
        []
      if (block.id !== existingNode.key) {
        treeManager.deleteNode(existingNode.value.id)
      }
      treeManager.upsertNode(block, {
        parentKey: existingNode.parentKey,
        order: br.order,
      })
      if (debug) {
        console.log('will reparent prevChildrenKeys', prevChildrenKeys)
      }
      if (block.id !== existingNode.key) {
        /** Then re-parent child nodes */
        for (const child of prevChildrenKeys) {
          if (!treeManager.getNode(child)) continue
          if (debug) {
            console.log('reparenting child', child, 'to', block.id)
          }
          treeManager.updateNode(child, { parentKey: block.id })
        }
      }
    } else {
      treeManager.addNode(block, { parentKey: br.parentId, order: br.order })
    }
  })

  perfReporter?.checkpoint('[fast forward] upserted first pass')

  await pMap(upserted, async ({ br }) => {
    const existingNode = treeManager.getNode(br.blockId)
    if (existingNode) {
      const parentNode = treeManager.getNode(br.parentId)
      const parentKey = parentNode?.key ?? br.parentId
      if (parentKey !== existingNode.parentKey) {
        treeManager.updateNode(existingNode.key, { parentKey })
      }
    }
  })

  perfReporter?.checkpoint('[fast forward] upserted second pass')

  /** Process deletes. Use the mutator to handle reference detaching, CoW, and the likes. */
  const { undo: deleteUndo } = await deleteBlocks(tx, {
    blockIds: deleted,
    isoNow: new Date().toISOString(),
    idSeed: getId(),
    rootId: rootBlockId,
    ignoreInstanceChildCheck: true,
  })

  perfReporter?.checkpoint('[fast forward] deleted')

  undo?.push(...deleteUndo)
}
